<script setup lang="ts">
import type { AxiosError } from 'axios'
import { ROUTES } from '@configs'
import type { BoardItemDetail } from '@store'

definePageMeta({
  layout: 'one-column',
})

useScrollRestorationNested()

const { locale } = useI18n()
const etcStore = useEtcStore()
const route = useRoute()
const notice = ref<BoardItemDetail>()
const err = ref<AxiosError>()
const { gtEvent } = useGoogleTag()
const { t } = useI18n()

watch(
  () => locale.value,
  () => {
    setTimeout(async () => {
      notice.value = await etcStore.fetchBoardDetail({
        nttGroupId: String(route.params.id),
        type: 'notice',
      })
    })
  }
)

const { data: _notice } = await useAsyncData('notice', async () => {
  try {
    const result = await etcStore.fetchBoardDetail({
      nttGroupId: String(route.params.id),
      type: 'notice',
    })
    return Promise.resolve(result)
  } catch (_err) {
    err.value = _err as AxiosError
    return Promise.reject(_err)
  }
})

if (_notice.value) {
  useSetSEO({
    useHeadArgs: [
      {
        title: _notice.value.nttSj,
        meta: [
          {
            name: 'description',
            content: useGetMaxThreeParagraphTexts(_notice.value.nttCn),
          },
        ],
      },
    ],
    useSeoMetaArgs: [
      {
        ogTitle: _notice.value.nttSj,
        ogDescription: useGetMaxThreeParagraphTexts(_notice.value.nttCn),
        ogImage: useGetFirstImageSource(_notice.value.nttCn),
      },
    ],
  })
  notice.value = _notice.value
}

if (err.value) {
  throw createError({
    statusCode: 404,
    statusMessage: `notice ${route.params.id} is not find`,
  })
}

const onChangePage = () => {
  gtEvent('clickEvent', {
    eventCategory: '클릭',
    eventAction: '공지사항 상세 > 목록으로 이동',
    eventLabel: t('board.label.list'),
    eventSlot: '공지사항 상세 페이지',
    eventI18nAddr: useKoreanTranslation('board.label.list'),
    eventComponent: 'Button',
  })
  useNavigations({ url: ROUTES.BOARD_NOTICE.path, query: route.query })
}
</script>

<template>
  <div class="board-wrapper">
    <NovaBoxBoardSideMenu :active="'notice'" />

    <NovaBoxBoardDetail
      :board-title="$t('board.title.notice')"
      :board-detail="notice!"
    >
      <template #actions>
        <NovaButtonText
          :label="$t('board.label.list')"
          :theme="'primary-blue'"
          :width="100"
          class="btn-action"
          @click="onChangePage"
        />
      </template>

      <template #links>
        <NovaBoxBoardDetailLink
          v-if="notice?.prev"
          :direction="'prev'"
          :board-item="notice!.prev!"
          :link-path="useRoutePathIdChange(ROUTES.BOARD_NOTICE_DETAIL.path, { id: notice!.prev!.nttGroupId })"
          :ga-slot="$t('board.title.notice')"
        />

        <NovaBoxBoardDetailLink
          v-if="notice?.next"
          :direction="'next'"
          :board-item="notice!.next!"
          :link-path="useRoutePathIdChange(ROUTES.BOARD_NOTICE_DETAIL.path, { id: notice!.next!.nttGroupId })"
          :ga-slot="$t('board.title.notice')"
        />
      </template>
    </NovaBoxBoardDetail>
  </div>
</template>

<style lang="scss" scoped>
.board-wrapper {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 40px;
  padding-top: 20px;
  color: $color-text-2;

  @include mobile {
    padding: 15px 20px;
    flex-direction: column;
    gap: 20px;
  }
}
</style>
